<template>
  <!-- The handshake sheet's code face, typing the other player's code back by itself. -->
  <div class="code-sheet" aria-hidden="true">
    <div class="code-top">
      <span class="code-faces">
        <span class="code-face is-me">
          <GamIcon :name="GamIconName.PAWN_EMPTY" size="22px" is-current />
        </span>
        <span class="code-face" :class="{ 'is-open': !isPaired }">
          <img v-if="isPaired" :src="faceSrc" alt="" />
          <GamIcon v-else :name="GamIconName.PAWN_EMPTY" size="22px" is-current />
        </span>
      </span>
    </div>

    <div class="code-pair">
      <span class="code-value">{{ myCode }}</span>
      <span class="code-mark">
        <GamIcon :name="GamIconName.HANDSHAKE_EMPTY" size="5cqmin" is-current />
      </span>
      <span class="code-typed" :class="{ 'is-done': isPaired }">{{ typed }}</span>
    </div>
  </div>
</template>

<script setup lang="ts">
import GamIcon from '@/views/components/GamIcon.vue';
import { GamIconName } from '@/views/composables/constants/components/gamIcon.constants';
import { MEMBER_CODE_LENGTH } from '@/views/main/handshakes/utils/handshake-code';
import { computed, onBeforeUnmount, ref, watch } from 'vue';

const props = defineProps<{
  /** The face that takes the open seat once its code is in. */
  faceSrc: string;
  /** The card is the one in front, so the code is typed again from its first character. */
  active: boolean;
}>();

const KEY_MS = 420;
const PAIRED_HOLD_MS = 2200;
const LETTERS = 'abcdefgh';
const DIGITS = '12345678';

/** A member code's shape: letter, digit, letter, digit, from the board's own letters and numbers. */
function memberCode(): string {
  return Array.from({ length: MEMBER_CODE_LENGTH }, (_, index) => {
    const alphabet = index % 2 === 0 ? LETTERS : DIGITS;
    return alphabet[Math.floor(Math.random() * alphabet.length)];
  }).join('');
}

const myCode = ref(memberCode());
const theirCode = ref(memberCode());
const typed = ref('');
const isPaired = computed(() => typed.value.length === MEMBER_CODE_LENGTH);
let timer: ReturnType<typeof setTimeout> | undefined;

function stopTyping() {
  if (timer == null) return;
  clearTimeout(timer);
  timer = undefined;
}

/** The first character is already on the line. The rest follow, then the line starts again. */
function beginTyping() {
  stopTyping();
  myCode.value = memberCode();
  theirCode.value = memberCode();
  typed.value = theirCode.value.slice(0, 1);
  timer = setTimeout(typeNext, KEY_MS);
}

function typeNext() {
  if (!props.active) return;
  const next = theirCode.value.slice(0, typed.value.length + 1);
  typed.value = next;
  if (next.length >= theirCode.value.length) {
    timer = setTimeout(() => {
      if (props.active) beginTyping();
    }, PAIRED_HOLD_MS);
    return;
  }
  timer = setTimeout(typeNext, KEY_MS);
}

watch(
  () => props.active,
  (active) => {
    if (!active) {
      stopTyping();
      typed.value = '';
      return;
    }
    beginTyping();
  },
  { immediate: true },
);

onBeforeUnmount(() => stopTyping());
</script>

<style lang="scss" scoped>
/* The handshake sheet's board, with the code face's three rows: the pair on top, the codes on the middle line. */
.code-sheet {
  --code-size: 14cqmin;
  display: grid;
  grid-template-rows: minmax(0, 1fr) auto minmax(0, 1fr);
  justify-items: center;
  align-items: center;
  box-sizing: border-box;
  width: min(100%, 420px);
  aspect-ratio: 1;
  padding: var(--spacing-h);
  overflow: hidden;
  border-radius: calc(36 / 420 * 100%);
  background-image: repeating-conic-gradient(var(--color-dark-700) 0% 25%, var(--color-dark-600) 0% 50%);
  background-size: 25% 25%;
  box-shadow:
    0 0 0 0.5px rgba(255, 255, 255, 0.08),
    0 2px 4px rgba(0, 0, 0, 0.45),
    0 18px 40px rgba(0, 0, 0, 0.55);
  container-type: size;
}

.code-top {
  align-self: start;
}

.code-faces {
  --face: var(--code-size);
  display: flex;
}

.code-face {
  display: flex;
  align-items: center;
  justify-content: center;
  width: var(--face);
  height: var(--face);
  overflow: hidden;
  border-radius: 50%;
  background: var(--color-dark-500);
  color: var(--color-white);

  img {
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 14%;
    object-fit: contain;
  }

  & + & {
    margin-left: calc(var(--face) * -0.2);
    box-shadow: 0 0 0 2px var(--color-dark-700);
  }

  &.is-me {
    background: var(--color-not-played);
    color: var(--color-white-30);
  }

  /* The seat the code fills, ringed pink until it is in. */
  &.is-open {
    background: var(--color-not-played);
    color: var(--color-white-30);
    box-shadow: 0 0 0 3px var(--color-pink);
  }
}

.code-pair {
  display: grid;
  grid-template-columns: 1fr auto 1fr;
  align-items: end;
  column-gap: 2.5cqmin;
  width: 100%;
}

.code-value,
.code-typed {
  font-family: 'Hanken Grotesk', monospace;
  font-size: var(--code-size);
  font-weight: 700;
  line-height: 1.1;
  letter-spacing: 0.17em;
  text-indent: 0.17em;
  color: var(--color-pink);
}

.code-value {
  justify-self: end;
}

.code-mark {
  display: flex;
  align-self: center;
  color: var(--color-white);
}

/* The typing line: four characters wide whatever is on it, so the code grows into it. */
.code-typed {
  justify-self: start;
  box-sizing: border-box;
  width: calc(4ch + 4 * 0.17em);
  min-height: 1.1em;
  border-bottom: 2px solid var(--color-pink);
  transition:
    color var(--transition),
    border-color var(--transition);

  &.is-done {
    border-bottom-color: transparent;
    color: var(--color-white);
  }
}
</style>
